import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { ActivityDebugSettings } from "./api-activity-debug-settings";
import { Download, RefreshCw, Settings2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PageFrame } from "@/components/admin/page-frame";
import { PageHeader } from "@/components/admin/page-header";
import { CopyIdButton } from "@/components/admin/copy-id-button";
import { EmptyState, InlineAlert } from "@/components/admin/feedback";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Combobox } from "@/components/ui/combobox";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  activityApi,
  apiErrorMessage,
  ApiError,
  type ActivityFilters,
  type ActivitySettings,
} from "@/lib/api";
import { useCurrentIdentityQuery, useProductsQuery } from "@/lib/queries";
import { useTimezone } from "@/lib/timezone";
import { APIActivityFiltersOutcome, APIActivityFiltersFailureStage } from "@/lib/generated/model";
import type {
  APIActivityEvent,
  APIActivityPage as ActivityPage,
} from "@/lib/generated/model";

type Draft = Omit<
  ActivityFilters,
  "status" | "operation" | "license_id" | "activation_id" | "request_id"
> & {
  product: string;
  window: string;
  status: string;
  operation: string;
  license_id: string;
  activation_id: string;
  request_id: string;
  from?: string;
  to?: string;
};
type ActivityEvent = APIActivityEvent;
type ActivityList = Omit<ActivityPage, "items"> & { items: ActivityEvent[] };
const initialDraft: Draft = {
  product: "all",
  window: "30",
  status: "",
  operation: "",
  license_id: "",
  activation_id: "",
  request_id: "",
};

const HTTP_STATUSES = [
  "200",
  "201",
  "204",
  "400",
  "401",
  "403",
  "404",
  "409",
  "422",
  "429",
  "500",
  "502",
  "503",
];

function activityStatus(value: string): number | undefined {
  return /^[1-5]\d{2}$/.test(value) ? Number(value) : undefined;
}

function draftReady(value: Draft) {
  if (value.window === "custom" && (!value.from || !value.to)) return false;
  return value.status === "" || activityStatus(value.status) !== undefined;
}

function snapshotDraft(value: Draft): Draft {
  const to =
    value.window === "custom" && value.to
      ? value.to
      : new Date().toISOString();
  return {
    ...value,
    to,
    from:
      value.window === "custom"
        ? value.from
        : new Date(Date.parse(to) - Number(value.window) * 60000).toISOString(),
  };
}

function matchesFilters(event: ActivityEvent, value: ActivityFilters) {
  const capturedAt = Date.parse(event.captured_at);
  return (
    (!value.from || capturedAt >= Date.parse(value.from)) &&
    (!value.to || capturedAt <= Date.parse(value.to)) &&
    (!value.unassigned || !event.product_id) &&
    (value.unassigned || !value.product_id || event.product_id === value.product_id) &&
    (!value.status || event.status === value.status) &&
    (!value.operation || event.operation === value.operation) &&
    (!value.license_id || event.license_id === value.license_id) &&
    (!value.activation_id || event.activation_id === value.activation_id) &&
    (!value.request_id || event.request_id === value.request_id) &&
    (!value.correlation_id || event.correlation_id === value.correlation_id) &&
    (!value.error_code || event.error_code === value.error_code) &&
    (!value.outcome || event.outcome === value.outcome) &&
    (!value.failure_stage || event.failure_stage === value.failure_stage)
  );
}

export function APIActivityPage() {
  const { t } = useTranslation();
  const identity = useCurrentIdentityQuery();
  const allowed =
    identity.data?.roles.includes("Security Admin") === true &&
    !identity.isError;
  return (
    <PageFrame size="fluid">
      {allowed ? (
        <ActivityConsole key={identity.data?.actor_id} />
      ) : (
        <>
          <PageHeader title={t("activity.title")} />
          <EmptyState
            title={t("activity.restricted")}
            message={t("activity.restrictedHint")}
          />
        </>
      )}
    </PageFrame>
  );
}

function ActivityConsole() {
  const { t } = useTranslation();
  const { timezoneAbbr, formatDateTime } = useTimezone();
  const products = useProductsQuery();
  const [settings, setSettings] = useState<ActivitySettings>();
  const [saved, setSaved] = useState<ActivitySettings>();
  const [page, setPage] = useState<ActivityList>({ items: [] });
  const [detail, setDetail] = useState<ActivityEvent>();
  // An Audit Log pivot opens one request's correlation over the widest window.
  const [pivotDraft] = useState<Draft>(() => {
    const correlation = new URLSearchParams(window.location.search).get("correlation_id")?.trim();
    return correlation ? { ...initialDraft, window: "10080", correlation_id: correlation } : initialDraft;
  });
  const [draft, setDraft] = useState<Draft>(pivotDraft);
  const [submitted, setSubmitted] = useState<Draft>(pivotDraft);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [format, setFormat] = useState<"csv" | "json">("csv");
  const [exportOpen, setExportOpen] = useState(false);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [detailError, setDetailError] = useState("");
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSucceeded, setSaveSucceeded] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [denied, setDenied] = useState(false);
  const [selectedID, setSelectedID] = useState("");
  const [selectionNotice, setSelectionNotice] = useState("");
  const [now, setNow] = useState(Date.now());
  const selected = useRef("");
  const selectedExpiresAt = useRef("");
  const generation = useRef(0);
  const detailGeneration = useRef(0);
  const actionGeneration = useRef(0);
  const exportGeneration = useRef(0);
  const mounted = useRef(true);
  const offset = useRef(0);
  const deniedRef = useRef(false);
  const options = (products.data?.products ?? []).map((product) => ({
    value: product.id,
    label: product.name,
  }));
  const clearSensitive = () => {
    generation.current++;
    detailGeneration.current++;
    actionGeneration.current++;
    exportGeneration.current++;
    selected.current = "";
    selectedExpiresAt.current = "";
    setSelectedID("");
    setSelectionNotice("");
    setDetail(undefined);
    setDetailLoading(false);
    setPage({ items: [] });
    setSettings(undefined);
    setSaved(undefined);
    setSaveSucceeded(false);
    setReason("");
    setDraft(initialDraft);
    setSubmitted(initialDraft);
    setExportOpen(false);
  };
  const forbidden = (err: unknown) => {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      deniedRef.current = true;
      setDenied(true);
      clearSensitive();
    }
  };
  function filters(value: Draft): ActivityFilters {
    const productID = value.product.startsWith("product:")
      ? value.product.slice("product:".length)
      : undefined;
    const to = value.to ?? new Date().toISOString();
    return {
      product_id: productID,
      unassigned: value.product === "unassigned" || undefined,
      status: activityStatus(value.status),
      operation: value.operation.trim() || undefined,
      license_id: value.license_id.trim() || undefined,
      activation_id: value.activation_id.trim() || undefined,
      request_id: value.request_id.trim() || undefined,
      correlation_id: value.correlation_id?.trim() || undefined,
      error_code: value.error_code?.trim() || undefined,
      outcome: value.outcome || undefined,
      failure_stage: value.failure_stage || undefined,
      source_ip: value.source_ip?.trim() || undefined,
      order: value.order || "desc",
      from:
        value.from ??
        new Date(Date.parse(to) - Number(value.window) * 60000).toISOString(),
      to,
    };
  }
  async function refresh(cursor = "", nextDraft = submitted, notice = "") {
    if (saving) return;
    if (!cursor) nextDraft = snapshotDraft(nextDraft);
    if ((nextDraft.source_ip ?? "") !== (submitted.source_ip ?? "")) {
      ++detailGeneration.current;
      ++exportGeneration.current;
      selected.current = "";
      selectedExpiresAt.current = "";
      setSelectedID("");
      setDetail(undefined);
      setDetailLoading(false);
      setPage({ items: [] });
      setExportOpen(false);
      setExporting(false);
      setReason("");
    }
    const selectionID = selected.current;
    const version = ++generation.current;
    setBusy(true);
    setError("");
    setDetailError("");
    try {
      const [next, result] = await Promise.all([
        activityApi.settings(),
        activityApi.list({
          ...filters(nextDraft),
          cursor: cursor || undefined,
          limit: 50,
        }),
      ]);
      if (
        !mounted.current ||
        deniedRef.current ||
        version !== generation.current
      )
        return;
      if (next.server_time)
        offset.current = Date.parse(next.server_time) - Date.now();
      setSaved(next);
      setSettings((current) => current ?? next);
      let selectionPresent = result.items.some(
        (item) => item.id === selectionID,
      );
      if (selectionID && !selectionPresent && !nextDraft.source_ip) {
        try {
          const selectedEvent = await activityApi.detail(selectionID, false);
          selectionPresent = matchesFilters(selectedEvent, filters(nextDraft));
        } catch (err) {
          if (!(err instanceof ApiError && err.status === 404)) throw err;
        }
        if (!mounted.current || deniedRef.current || version !== generation.current)
          return;
      }
      const serverNow = Date.now() + offset.current;
      if (nextDraft.source_ip && result.items.some((item) => !item.debug_expires_at || Date.parse(item.debug_expires_at) <= serverNow)) {
        clearFilters(t("activity.ipExpired"));
        return;
      }
      setPage({ ...result, items: result.items.map((item) => protectedAt(item, serverNow)) });
      setSubmitted(nextDraft);
      setNow(Date.now());
      if (selected.current !== selectionID) return;
      if (selectionID && !selectionPresent) {
        ++detailGeneration.current;
        selected.current = "";
        selectedExpiresAt.current = "";
        setSelectedID("");
        setDetail(undefined);
        setDetailLoading(false);
        setSelectionNotice(t("activity.selectionCleared"));
      } else {
        setSelectionNotice(notice);

      }
    } catch (err) {
      if (mounted.current && version === generation.current) {
        if (err instanceof ApiError && err.code === "activity_ip_association_expired") {
          clearFilters(t("activity.ipExpired"));
          return;
        }
        forbidden(err);
        setError(apiErrorMessage(err));
      }
    } finally {
      if (mounted.current && version === generation.current) setBusy(false);
    }
  }
  async function choose(id: string, expiresAt = selectedExpiresAt.current) {
    selected.current = id;
    selectedExpiresAt.current = expiresAt;
    setSelectedID(id);
    setDetail(undefined);
    setDetailError("");
    setDetailLoading(true);
    setSelectionNotice("");
    const version = ++detailGeneration.current;
    try {
      const result = await activityApi.detail(id);
      if (
        mounted.current &&
        version === detailGeneration.current &&
        selected.current === id
      ) {
        selectedExpiresAt.current = result.expires_at;
        if (Date.parse(result.expires_at) - offset.current <= Date.now()) {
          selected.current = "";
          selectedExpiresAt.current = "";
          setSelectedID("");
          setDetail(undefined);
          setSelectionNotice(t("activity.selectionCleared"));
        } else {
          setDetail(protectedAt(result, Date.now() + offset.current));
        }
      }
    } catch (err) {
      if (mounted.current && version === detailGeneration.current) {
        forbidden(err);
        if (err instanceof ApiError && err.status === 404) {
          ++detailGeneration.current;
          selected.current = "";
          selectedExpiresAt.current = "";
          setSelectedID("");
          setDetail(undefined);
          setDetailLoading(false);
          setSelectionNotice(t("activity.selectionCleared"));
        } else {
          setDetailError(apiErrorMessage(err));
        }
      }
    } finally {
      if (mounted.current && version === detailGeneration.current) {
        setDetailLoading(false);
      }
    }
  }
  function filterBy(field: "license_id" | "activation_id" | "request_id" | "correlation_id", value: string) {
    const next = { ...submitted, [field]: value, ...(field === "activation_id" ? { order: "asc" as const } : {}) };
    setDraft(next);
    void refresh("", next);
  }
  function clearFilters(notice = "") {
    ++detailGeneration.current;
    ++exportGeneration.current;
    selected.current = "";
    selectedExpiresAt.current = "";
    setSelectedID("");
    setDetail(undefined);
    setDetailLoading(false);
    setExportOpen(false);
    setExporting(false);
    setReason("");
    setPage({ items: [] });
    setSubmitted(initialDraft);
    setDraft(initialDraft);
    setSelectionNotice(notice);
    void refresh("", initialDraft, notice);
  }
  useEffect(() => {
    mounted.current = true;
    void refresh();
    return () => {
      mounted.current = false;
      generation.current++;
      detailGeneration.current++;
    }; // Manual refresh deliberately owns all activity requests.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const expiresAt = [...page.items, ...(detail ? [detail] : [])]
    .map((item) => Date.parse(item.expires_at) - offset.current)
    .filter((expiry) => Number.isFinite(expiry) && expiry > now)
    .sort((a, b) => a - b)[0];
  useEffect(() => {
    if (!expiresAt) return;
    const timer = window.setTimeout(
      () => setNow(Date.now()),
      Math.max(1, expiresAt - Date.now()),
    );
    return () => window.clearTimeout(timer);
  }, [expiresAt]);
  useEffect(() => {
    if (!selectedID || !selectedExpiresAt.current) return;
    const expiry = Date.parse(selectedExpiresAt.current) - offset.current;
    const timer = window.setTimeout(() => {
      if (selected.current !== selectedID) return;
      ++detailGeneration.current;
      selected.current = "";
      selectedExpiresAt.current = "";
      setSelectedID("");
      setDetail(undefined);
      setDetailLoading(false);
      setSelectionNotice(t("activity.selectionCleared"));
    }, Math.max(1, expiry - Date.now()));
    return () => window.clearTimeout(timer);
  }, [detail?.expires_at, selectedID, t]);
  const debugDeadline = [...page.items, ...(detail ? [detail] : [])]
    .map((item) => item.debug_expires_at ? Date.parse(item.debug_expires_at) - offset.current : NaN)
    .filter((expiry) => Number.isFinite(expiry) && expiry > now)
    .sort((a, b) => a - b)[0];
  useEffect(() => {
    if (!debugDeadline) return;
    const timer = window.setTimeout(() => {
      const serverNow = Date.now() + offset.current;
      ++detailGeneration.current;
      ++exportGeneration.current;
      setDetailLoading(false);
      setExporting(false);
      setExportOpen(false);
      setReason("");
      setDetail((current) => current && protectedAt(current, serverNow));
      if (submitted.source_ip) {
        // Membership itself reveals the protected IP association.
        ++generation.current;
        setPage({ items: [] });
        setDetail(undefined);
        selected.current = "";
        setSelectedID("");
        setDraft((current) => ({ ...current, source_ip: undefined }));
        setSubmitted((current) => ({ ...current, source_ip: undefined }));
        setSelectionNotice(t("activity.ipExpired"));
        setBusy(false);
      } else {
        setPage((current) => ({ ...current, items: current.items.map((item) => protectedAt(item, serverNow)) }));
      }
      setNow(Date.now());
    }, Math.max(1, debugDeadline - Date.now()));
    return () => window.clearTimeout(timer);
  }, [debugDeadline, submitted.source_ip, t]);
  async function save(event: FormEvent) {
    event.preventDefault();
    if (!settings || !reason.trim() || saving || exporting) return;
    // A settings write wins over any earlier settings/list refresh response.
    ++generation.current;
    setBusy(false);
    const version = ++actionGeneration.current;
    setSaving(true);
    setSaveSucceeded(false);
    setActionError("");
    try {
      const next = await activityApi.updateSettings({
        ...settings,
        expected_configuration_id: settings.id,
        reason: reason.trim(),
      });
      if (
        !mounted.current ||
        deniedRef.current ||
        version !== actionGeneration.current
      )
        return;
      setSettings(next);
      setSaved(next);
      setSaveSucceeded(true);
      setReason("");
    } catch (err) {
      if (mounted.current && version === actionGeneration.current) {
        forbidden(err);
        setActionError(apiErrorMessage(err));
        if (err instanceof ApiError && err.status === 409) {
          try {
            const latest = await activityApi.settings();
            if (mounted.current && !deniedRef.current && version === actionGeneration.current) {
              setSaved(latest);
              setSettings(latest);
              setReason("");
              setActionError(t("activity.settingsConflict"));
            }
          } catch (reloadError) { forbidden(reloadError); }
        }
      }
    } finally {
      if (mounted.current && version === actionGeneration.current)
        setSaving(false);
    }
  }
  async function exportEvents(event: FormEvent) {
    event.preventDefault();
    if (!reason.trim() || exporting || saving || busy) return;
    const version = ++exportGeneration.current;
    setExporting(true);
    setSaveSucceeded(false);
    setActionError("");
    try {
      const file = await activityApi.export({
        format,
        filters: filters(submitted),
        reason: reason.trim(),
      });
      if (
        !mounted.current ||
        deniedRef.current ||
        version !== exportGeneration.current
      )
        return;
      const url = URL.createObjectURL(file.bytes);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.filename;
      link.click();
      URL.revokeObjectURL(url);
      setReason("");
      setExportOpen(false);
    } catch (err) {
      if (mounted.current && version === exportGeneration.current) {
        if (err instanceof ApiError && err.code === "activity_ip_association_expired") {
          clearFilters(t("activity.ipExpired"));
          return;
        }
        forbidden(err);
        setActionError(apiErrorMessage(err));
      }
    } finally {
      if (mounted.current && version === exportGeneration.current)
        setExporting(false);
    }
  }
  if (denied)
    return (
      <>
        <PageHeader title={t("activity.title")} />
        <EmptyState
          title={t("activity.restricted")}
          message={t("activity.restrictedHint")}
        />
      </>
    );
  const primary =
    "border-ink bg-ink text-canvas hover:bg-ink/90 hover:text-canvas";
  return (
    <>
      <PageHeader
        title={t("activity.title")}
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => void refresh()}
              disabled={busy || saving}
            >
              <RefreshCw className="size-4" aria-hidden="true" />
              {t("common.refresh")}
            </Button>
            <Button
              className={primary}
              onClick={() => {
                setReason("");
                setSaveSucceeded(false);
                setExportOpen(true);
              }}
              disabled={!page.items.length || saving || exporting || busy}
            >
              <Download className="size-4" aria-hidden="true" />
              {t("activity.export")}
            </Button>
          </>
        }
      />
      {error && (
        <InlineAlert
          title={t("activity.unavailable")}
          message={error}
          onRetry={() => void refresh()}
        />
      )}
      <section
        className="border border-line bg-panel"
        aria-labelledby="activity-settings-title"
      >
        <button
          type="button"
          className="flex w-full cursor-pointer items-center justify-between p-3 text-left focus-visible:outline-2 focus-visible:outline-focus"
          aria-expanded={settingsOpen}
          onClick={() => setSettingsOpen((open) => !open)}
        >
          <span
            id="activity-settings-title"
            className="inline-flex items-center gap-2 font-medium"
          >
            <Settings2 className="size-4" aria-hidden="true" />
            {t("activity.settings")}
          </span>
          <span className="text-sm text-muted">
            {saved?.enabled ? t("activity.enabled") : t("activity.disabled")}
          </span>
        </button>
        {settingsOpen && settings && (
          <form
            className="grid gap-3 border-t border-line p-3"
            onSubmit={(event) => void save(event)}
            onChange={() => setSaveSucceeded(false)}
          >
            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                variant="outline"
                aria-pressed={settings.enabled}
                onClick={() => {
                  setSaveSucceeded(false);
                  setSettings({ ...settings, enabled: !settings.enabled });
                }}
              >
                {settings.enabled
                  ? t("activity.enabled")
                  : t("activity.disabled")}
              </Button>
              <span className="text-sm text-muted">
                {t("activity.captureHint")}
              </span>
            </div>
            <fieldset className="grid gap-2">
              <legend className="text-sm font-medium">
                {t("activity.captureScope")}
              </legend>
              <div className="flex flex-wrap gap-3">
                <label>
                  <input
                    className="mr-2 size-4 accent-ink"
                    type="radio"
                    checked={settings.scope === "all"}
                    onChange={() =>
                      setSettings({
                        ...settings,
                        scope: "all",
                        product_ids: [],
                      })
                    }
                  />
                  {t("activity.allProducts")}
                </label>
                <label>
                  <input
                    className="mr-2 size-4 accent-ink"
                    type="radio"
                    checked={settings.scope === "selected"}
                    onChange={() =>
                      setSettings({ ...settings, scope: "selected" })
                    }
                  />
                  {t("activity.selectedProducts")}
                </label>
              </div>
            </fieldset>
            {settings.scope === "selected" && (
              <fieldset className="grid gap-2">
                <legend className="text-sm font-medium">
                  {t("activity.productsToCapture")}
                </legend>
                <div className="flex flex-wrap gap-x-4 gap-y-2">
                  {options.map((option) => (
                    <label key={option.value} className="text-sm">
                      <input
                        className="mr-2 size-4 accent-ink"
                        type="checkbox"
                        checked={settings.product_ids.includes(option.value)}
                        onChange={(event) =>
                          setSettings({
                            ...settings,
                            product_ids: event.target.checked
                              ? [...settings.product_ids, option.value]
                              : settings.product_ids.filter(
                                  (id) => id !== option.value,
                                ),
                          })
                        }
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </fieldset>
            )}
            <label className="grid max-w-xs gap-1 text-sm">
              {t("activity.retentionHours")}
              <Input
                type="number"
                min={1}
                max={168}
                value={settings.ledger_retention_hours ?? settings.retention_hours}
                onChange={(event) =>
                  setSettings({
                    ...settings,
                    ledger_retention_hours: Number(event.target.value),
                    retention_hours: Number(event.target.value),
                  })
                }
              />
            </label>
            <ActivityDebugSettings value={settings} onChange={setSettings} products={options} />
            <p className="text-xs text-muted">
              {t("activity.limits", {
                retention: settings.retention_hours,
                dropped: saved?.dropped ?? 0,
              })}
            </p>
            <label className="grid gap-1 text-sm">
              {t("activity.reason")}
              <Input
                required
                maxLength={512}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </label>
            <p role="status" className={saveSucceeded ? "text-sm text-ink" : "sr-only"}>
              {saveSucceeded ? t("activity.settingsSaved") : ""}
            </p>
            {actionError && (
              <InlineAlert
                title={t("activity.actionFailed")}
                message={actionError}
              />
            )}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={saving}
                onClick={() => {
                  setSaveSucceeded(false);
                  if (saved) setSettings(saved);
                  setReason("");
                  setActionError("");
                }}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                className={primary}
                disabled={
                  saving ||
                  exporting ||
                  !reason.trim() ||
                  (settings.scope === "selected" && !settings.product_ids.length) ||
                  (settings.debug_scope === "selected" && !settings.debug_product_ids?.length) ||
                  (settings.debug_retention_hours ?? 24) > (settings.ledger_retention_hours ?? settings.retention_hours) ||
                  Boolean(settings.debug_starts_at && settings.debug_ends_at && Date.parse(settings.debug_starts_at) >= Date.parse(settings.debug_ends_at))
                }
              >
                {saving ? t("common.saving") : t("common.save")}
              </Button>
            </div>
          </form>
        )}
      </section>
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (!draftReady(draft)) return;
          void refresh("", draft);
        }}
        role="search"
        aria-label={t("activity.filters")}
      >
        <FilterField label={t("activity.product")}>
          <Select
            value={draft.product}
            onValueChange={(product) => setDraft({ ...draft, product })}
            options={[
              { value: "all", label: t("activity.allProducts") },
              { value: "unassigned", label: t("activity.unassigned") },
              ...options.map((option) => ({
                ...option,
                value: `product:${option.value}`,
              })),
            ]}
            className="min-w-40"
          />
        </FilterField>
        <FilterField label={t("activity.window")}>
          <Select
            value={draft.window}
            onValueChange={(window) =>
              setDraft({
                ...draft,
                window,
                from: window === "custom" ? draft.from : undefined,
                to: window === "custom" ? draft.to : undefined,
              })
            }
            options={[
              { value: "30", label: t("activity.last30") },
              { value: "60", label: t("activity.last60") },
              { value: "1440", label: t("activity.last24") },
              { value: "10080", label: t("activity.last7") },
              { value: "custom", label: t("activity.customRange") },
            ]}
          />
        </FilterField>
        {draft.window === "custom" && (
          <>
            <FilterField label={t("activity.from")}>
              <DateTimePicker
                id="activity-from"
                value={draft.from ?? ""}
                onValueChange={(from) => setDraft({ ...draft, from: from || undefined })}
                aria-label={`${t("activity.from")} (${timezoneAbbr})`}
              />
            </FilterField>
            <FilterField label={t("activity.to")}>
              <DateTimePicker
                id="activity-to"
                value={draft.to ?? ""}
                onValueChange={(to) => setDraft({ ...draft, to: to || undefined })}
                aria-label={`${t("activity.to")} (${timezoneAbbr})`}
              />
            </FilterField>
          </>
        )}
        <FilterField label={t("activity.status")}>
          <Combobox
            aria-label={t("activity.status")}
            value={draft.status}
            onValueChange={(status) => setDraft({ ...draft, status })}
            options={HTTP_STATUSES}
            placeholder={t("activity.allStatuses")}
            emptyLabel={t("activity.statusEmpty")}
            triggerLabel={t("activity.statusSuggestions")}
            className="min-w-32 rounded-full px-4"
          />
        </FilterField>
        <TextFilter
          label={t("activity.operation")}
          value={draft.operation}
          onChange={(operation) => setDraft({ ...draft, operation })}
        />
        <FilterField>
          <details>
            <summary className="flex h-9 cursor-pointer list-none items-center rounded-full border border-control px-4 [&::-webkit-details-marker]:hidden">
              {t("activity.idFilters")}
            </summary>
            <div className="mt-2 flex flex-wrap gap-2">
              <TextFilter
                label={t("activity.licenseId")}
                value={draft.license_id}
                onChange={(license_id) => setDraft({ ...draft, license_id })}
              />
              <TextFilter
                label={t("activity.activationId")}
                value={draft.activation_id}
                onChange={(activation_id) =>
                  setDraft({ ...draft, activation_id })
                }
              />
              <TextFilter
                label="Request ID"
                value={draft.request_id}
                onChange={(request_id) => setDraft({ ...draft, request_id })}
              />
              <TextFilter label="Correlation ID" value={draft.correlation_id ?? ""} onChange={(correlation_id) => setDraft({ ...draft, correlation_id })} />
              <TextFilter label={t("activity.errorCode")} value={draft.error_code ?? ""} onChange={(error_code) => setDraft({ ...draft, error_code })} />
              <FilterField label={t("activity.sourceIp")}>
                <Input maxLength={45} autoComplete="off" spellCheck={false} value={draft.source_ip ?? ""} onChange={(event) => setDraft({ ...draft, source_ip: event.target.value })} />
              </FilterField>
              <p className="w-full text-xs text-muted">{t("activity.ipSearchHint")}</p>
            </div>
          </details>
        </FilterField>
        <FilterField label={t("activity.outcome")}>
          <Select value={draft.outcome ?? "all"} onValueChange={(value) => setDraft({ ...draft, outcome: value === "all" ? undefined : value as ActivityFilters["outcome"] })}
            options={[{ value: "all", label: t("activity.anyValue") }, ...Object.values(APIActivityFiltersOutcome).map((value) => ({ value, label: value }))]} />
        </FilterField>
        <FilterField label={t("activity.failureStage")}>
          <Select value={draft.failure_stage ?? "all"} onValueChange={(value) => setDraft({ ...draft, failure_stage: value === "all" ? undefined : value as ActivityFilters["failure_stage"] })}
            options={[{ value: "all", label: t("activity.anyValue") }, ...Object.values(APIActivityFiltersFailureStage).map((value) => ({ value, label: value }))]} />
        </FilterField>
        <FilterField label={t("activity.order")}>
          <Select value={draft.order ?? "desc"} onValueChange={(value) => setDraft({ ...draft, order: value as "asc" | "desc" })}
            options={[{ value: "desc", label: t("activity.newestFirst") }, { value: "asc", label: t("activity.oldestFirst") }]} />
        </FilterField>
        <FilterField>
          <Button
            type="submit"
            variant="outline"
            disabled={busy || !draftReady(draft)}
          >
            {t("activity.apply")}
          </Button>
        </FilterField>
      </form>
      <div className="flex flex-wrap items-center gap-2 text-xs" aria-label={t("activity.appliedFilters")}>
          <span className="text-muted">{t("activity.appliedFilters")}</span>
          <span className="rounded-full border border-control px-2.5 py-1">
            {submitted.window === "custom"
              ? `${t("activity.from")} ${formatDateTime(submitted.from)} · ${t("activity.to")} ${formatDateTime(submitted.to)}`
              : t(`activity.${submitted.window === "30" ? "last30" : submitted.window === "1440" ? "last24" : submitted.window === "10080" ? "last7" : "last60"}`)}
          </span>
          {submitted.product !== "all" && <span className="rounded-full border border-control px-2.5 py-1">{submitted.product === "unassigned" ? t("activity.unassigned") : options.find((option) => `product:${option.value}` === submitted.product)?.label ?? submitted.product}</span>}
          {submitted.status && <span className="rounded-full border border-control px-2.5 py-1">HTTP {submitted.status}</span>}
          {submitted.operation && <span className="max-w-full truncate rounded-full border border-control px-2.5 py-1 font-mono">{submitted.operation}</span>}
          {submitted.license_id && <span className="max-w-full truncate rounded-full border border-control px-2.5 py-1 font-mono">{t("activity.licenseId")}: {submitted.license_id}</span>}
          {submitted.activation_id && <span className="max-w-full truncate rounded-full border border-control px-2.5 py-1 font-mono">{t("activity.activationId")}: {submitted.activation_id}</span>}
          {submitted.request_id && <span className="max-w-full truncate rounded-full border border-control px-2.5 py-1 font-mono">Request ID: {submitted.request_id}</span>}
          {[submitted.correlation_id, submitted.error_code, submitted.outcome, submitted.failure_stage, submitted.source_ip].filter(Boolean).map((value, index) => <span key={index} className="max-w-full break-all font-mono">{value}</span>)}
          <Button type="button" variant="ghost" size="sm" onClick={() => clearFilters()}>{t("activity.clearAll")}</Button>
      </div>
      {page.coverage && <section className="border-y border-line py-3 text-xs" aria-label={t("activity.coverage")}>
        <dl className="flex flex-wrap gap-x-6 gap-y-2">
          <div><dt className="text-muted">{t("activity.coverage")}</dt><dd>{page.coverage.capture_state} · {page.coverage.configuration.scope} · {page.coverage.configuration.product_ids.join(", ")}</dd></div>
          <div><dt className="text-muted">{t("activity.retained")}</dt><dd>{page.coverage.retained} / {page.coverage.capacity}</dd></div>
          <div><dt className="text-muted">{t("activity.oldestRetained")}</dt><dd>{formatDateTime(page.coverage.oldest_retained_at, t("activity.unknown"))}</dd></div>
          <div><dt className="text-muted">{t("activity.capacityEvictions")}</dt><dd>{page.coverage.configuration.capacity_evictions ?? 0}</dd></div>
          <div><dt className="text-muted">Ledger / Debug</dt><dd>{page.coverage.configuration.ledger_retention_hours ?? page.coverage.configuration.retention_hours} h / {page.coverage.configuration.debug_retention_hours ?? 24} h</dd></div>
        </dl>
        <p className="pt-2 text-muted">{t("activity.coverageHint")}</p>
        <p className="pt-1 text-muted">{t("activity.observedDrops")}: {page.coverage.configuration.dropped}. {t("activity.clientLimit")}</p>
        <p className="pt-1 text-muted">Debug: {page.coverage.configuration.debug_enabled ? t("activity.enabled") : t("activity.disabled")} · {page.coverage.configuration.debug_scope ?? "inherit"} · {page.coverage.configuration.debug_product_ids?.join(", ")} · {page.coverage.configuration.debug_operations?.join(", ") || t("activity.anyValue")} · {formatDateTime(page.coverage.configuration.debug_starts_at, t("activity.unbounded"))} / {formatDateTime(page.coverage.configuration.debug_ends_at, t("activity.unbounded"))}</p>
      </section>}
      <p className="text-xs text-muted">{t("activity.detailAuditHint")}</p>
      <p className={selectionNotice ? "text-xs text-muted" : "sr-only"} role="status">{selectionNotice}</p>
      {products.isError && (
        <InlineAlert
          title={t("activity.productsFailed")}
          message={apiErrorMessage(products.error)}
        />
      )}
      <div className="grid min-w-0 items-start gap-4 xl:grid-cols-[minmax(0,1fr)_24rem]">
        <div className="min-w-0">
          <Table>
            <caption className="sr-only">{t("activity.title")}</caption>
            <TableHeader>
              <TableRow>
                {["time", "product", "license", "device", "operation", "outcome", "status", "duration"].map(
                  (key) => (
                    <TableHead key={key}>
                      {t(`activity.${key}` as "activity.time")}
                    </TableHead>
                  ),
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {page.items
                .filter(
                  (item) => Date.parse(item.expires_at) > now + offset.current,
                )
                .map((item) => (
                  <TableRow
                    key={item.id}
                    data-state={selectedID === item.id ? "selected" : undefined}
                    tabIndex={busy ? -1 : 0}
                    aria-selected={selectedID === item.id}
                    aria-disabled={busy}
                    className="cursor-pointer focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus data-[state=selected]:bg-focus/10"
                    onClick={() => {
                      if (!busy) void choose(item.id, item.expires_at);
                    }}
                    onKeyDown={(event) => {
                      if (!busy && (event.key === "Enter" || event.key === " ")) {
                        event.preventDefault();
                        void choose(item.id, item.expires_at);
                      }
                    }}
                  >
                    <TableCell className="whitespace-nowrap font-mono text-xs">
                      {formatDateTime(item.captured_at)}
                    </TableCell>
                    <TableCell>
                      {item.product_id
                        ? (options.find(
                            (option) => option.value === item.product_id,
                          )?.label ?? item.product_id)
                        : t("activity.unassigned")}
                    </TableCell>
                    <TableCell>
                      <span className="block max-w-40 truncate font-mono text-xs">{item.license_id || "—"}</span>
                    </TableCell>
                    <TableCell>
                      <span className="block max-w-48 truncate">{item.computer_name || "—"}</span>
                      {item.activation_id && <span className="block max-w-48 truncate font-mono text-[11px] text-muted">{item.activation_id}</span>}
                    </TableCell>
                    <TableCell>
                      <span className="block max-w-40 truncate font-mono text-xs">
                        {item.operation || `${item.method} ${item.route}`}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      <span>{item.outcome ?? "unknown"}</span>
                      {item.failure_stage && <span className="block text-muted">{item.failure_stage}</span>}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {item.status ?? t("activity.unknown")}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {item.duration_ms} ms
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          {!busy &&
            !error &&
            !page.items.filter(
              (item) => Date.parse(item.expires_at) > now + offset.current,
            ).length && (
              <EmptyState
                title={
                  settings?.enabled
                    ? t("activity.waiting")
                    : t("activity.noResults")
                }
                message={
                  settings?.enabled
                    ? t("activity.scope")
                    : t("activity.offHint")
                }
              />
            )}
          {busy && (
            <p role="status" className="p-3 text-sm">
              {t("activity.loading")}
            </p>
          )}
          <div className="flex justify-end py-3">
            <Button
              variant="outline"
              size="sm"
              disabled={!page.next_cursor || busy}
              onClick={() => void refresh(page.next_cursor)}
            >
              {t("activity.next")}
            </Button>
          </div>
        </div>
        <aside
          className="min-w-0 border border-line bg-panel p-4"
          aria-label={t("activity.detail")}
        >
          {detailLoading ? (
            <p className="text-sm text-muted" role="status">{t("activity.loadingDetail")}</p>
          ) : detailError ? (
            <InlineAlert
              title={t("activity.detailFailed")}
              message={detailError}
            />
          ) : detail && Date.parse(detail.expires_at) > now + offset.current ? (
            <>
              <h2 className="break-words font-mono text-sm">
                {detail.operation || `${detail.method} ${detail.route}`}
              </h2>
              <p className="mt-2 font-medium">
                {detail.outcome ?? "unknown"} · HTTP {detail.status ?? t("activity.unknown")} · {detail.duration_ms} ms
              </p>
              <dl className="my-4 grid grid-cols-[6rem_minmax(0,1fr)] gap-2 text-xs">
                <dt>{t("activity.failureStage")}</dt><dd className="break-all font-mono">{detail.failure_stage || t("activity.unknown")}</dd>
                <dt>{t("activity.errorCode")}</dt><dd className="break-all font-mono">{detail.error_code || t("activity.unknown")}</dd>
                <dt>{t("activity.responseState")}</dt><dd>{detail.response_state ?? "unknown"}</dd>
                <dt>{t("activity.startedAt")}</dt><dd>{formatDateTime(detail.started_at, t("activity.unknown"))}</dd>
                <dt>{t("activity.completedAt")}</dt><dd>{formatDateTime(detail.completed_at, t("activity.unknown"))}</dd>
                <dt>{t("activity.activationTransition")}</dt><dd>{detail.activation_before ?? t("activity.unknown")} → {detail.activation_after ?? t("activity.unknown")}</dd>
                <dt>{t("activity.generationChanged")}</dt><dd>{detail.secret_generation_changed === undefined ? t("activity.unknown") : String(detail.secret_generation_changed)}</dd>
                <dt>{t("activity.ledgerExpiry")}</dt><dd>{formatDateTime(detail.expires_at)}</dd>
                <dt>Correlation ID</dt><dd className="break-all font-mono">{detail.correlation_id}</dd>
                <dt>Request ID</dt>
                <dd className="flex min-w-0 items-center gap-1"><span className="min-w-0 break-all font-mono">{detail.request_id}</span><CopyIdButton value={detail.request_id} label="Request ID" /></dd>
                <dt>{t("activity.licenseId")}</dt>
                <dd className="flex min-w-0 items-center gap-1"><span className="min-w-0 break-all font-mono">{detail.license_id || "—"}</span>{detail.license_id && <CopyIdButton value={detail.license_id} label={t("activity.licenseId")} />}</dd>
                <dt>{t("activity.activationId")}</dt>
                <dd className="flex min-w-0 items-center gap-1"><span className="min-w-0 break-all font-mono">{detail.activation_id || "—"}</span>{detail.activation_id && <CopyIdButton value={detail.activation_id} label={t("activity.activationId")} />}</dd>
                <dt>{t("activity.computerName")}</dt>
                <dd className="break-words">{detail.computer_name || "—"}</dd>
              </dl>
              {(detail.license_id || detail.activation_id) && <div className="mb-4 flex flex-wrap gap-2">
                {detail.license_id && <Button type="button" variant="outline" size="sm" onClick={() => filterBy("license_id", detail.license_id!)}>{t("activity.filterLicense")}</Button>}
                {detail.activation_id && <Button type="button" variant="outline" size="sm" onClick={() => filterBy("activation_id", detail.activation_id!)}>{t("activity.filterActivation")}</Button>}
              </div>}
              <div className="mb-4 flex flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => filterBy("request_id", detail.request_id)}>{t("activity.filterRequest")}</Button>
                {detail.correlation_id && <Button type="button" variant="outline" size="sm" onClick={() => filterBy("correlation_id", detail.correlation_id)}>{t("activity.filterCorrelation")}</Button>}
              </div>
              <p className="mb-4 text-xs text-muted">{t("activity.computerNameHint")}</p>
              <Payload title={t("activity.stageTimings")} value={detail.stage_timings} />
              <Payload title={t("activity.auditReferences")} value={detail.audit_event_ids} />
              {auditPivot(detail) && <a className="mb-4 block text-sm text-focus underline underline-offset-4" href={auditPivot(detail)}>{t("activity.relatedAudit")}</a>}
              {detail.license_id && <a className="mb-4 block text-sm text-focus underline underline-offset-4" href={`/admin/licenses/${encodeURIComponent(detail.license_id)}`}>{t("activity.openLicense")}</a>}
              {detail.product_id && <Button type="button" variant="outline" size="sm" onClick={() => {
                const next = { ...submitted, product: `product:${detail.product_id}` };
                setDraft(next);
                void refresh("", next);
              }}>{t("activity.filterProduct")}</Button>}
              <dl className="mb-4 grid grid-cols-[7rem_minmax(0,1fr)] gap-2 text-xs">
                <dt>{t("activity.protectedDetail")}</dt><dd>{detail.debug_availability ?? "unavailable"}</dd>
                <dt>{t("activity.debugExpiry")}</dt><dd>{formatDateTime(detail.debug_expires_at, t("activity.unknown"))}</dd>
                {detail.source_ip && <><dt>{t("activity.sourceIp")}</dt><dd className="break-all font-mono">{detail.source_ip} ({detail.ip_source})</dd></>}
              </dl>
              {detail.request_headers && <Payload title="Request headers" value={detail.request_headers} />}
              {detail.response_headers && <Payload title="Response headers" value={detail.response_headers} />}
              <Payload title="Request" value={detail.request} />
              <Payload title="Response" value={detail.response} />
              <p className="text-xs text-muted">{t("activity.redacted")}</p>
            </>
          ) : (
            <p className="text-sm text-muted">
              {selectedID ? t("activity.expired") : t("activity.selectRequest")}
            </p>
          )}
        </aside>
      </div>
      <Dialog
        open={exportOpen}
        onOpenChange={(open) => {
          if (!exporting) setExportOpen(open);
        }}
      >
        <DialogContent>
          <form onSubmit={(event) => void exportEvents(event)}>
            <DialogHeader>
              <DialogTitle>{t("activity.export")}</DialogTitle>
              <DialogDescription>{t("activity.exportHint")}</DialogDescription>
            </DialogHeader>
            <div className="my-4 grid gap-3">
              <label className="grid gap-1 text-sm">
                {t("activity.exportFormat")}
                <Select
                  value={format}
                  onValueChange={(value) => setFormat(value as "csv" | "json")}
                  options={[
                    { value: "csv", label: "CSV" },
                    { value: "json", label: "JSON" },
                  ]}
                />
              </label>
              <label className="grid gap-1 text-sm">
                {t("activity.reason")}
                <Input
                  required
                  maxLength={512}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
              </label>
              {actionError && (
                <InlineAlert
                  title={t("activity.actionFailed")}
                  message={actionError}
                />
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={exporting}
                onClick={() => setExportOpen(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                className={primary}
                disabled={exporting || saving || busy || !reason.trim()}
              >
                {t("activity.export")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
function FilterField({
  label,
  children,
}: {
  label?: string;
  children: ReactNode;
}) {
  const Tag = label ? "label" : "div";
  return (
    <Tag className="grid grid-rows-[1rem_auto] items-start gap-1 text-xs">
      <span className={label ? "leading-4" : "invisible leading-4"} aria-hidden={!label}>
        {label ?? "\u00a0"}
      </span>
      {children}
    </Tag>
  );
}

function TextFilter({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <FilterField label={label}>
      <Input
        value={value}
        maxLength={256}
        onChange={(event) => onChange(event.target.value)}
      />
    </FilterField>
  );
}

function Payload({ title, value }: { title: string; value: unknown }) {
  return (
    <section className="mb-4">
      <h3 className="mb-2 text-sm font-medium">{title}</h3>
      <pre className="console-scrollbar max-h-96 overflow-auto whitespace-pre border border-line bg-canvas p-3 font-mono text-xs leading-relaxed">
        {JSON.stringify(value ?? { payload: "unavailable" }, null, 2)}
      </pre>
    </section>
  );
}

function protectedAt(event: ActivityEvent, serverNow: number): ActivityEvent {
  if (!event.debug_expires_at || Date.parse(event.debug_expires_at) > serverNow) return event;
  return { ...event, request: undefined, response: undefined, request_headers: undefined,
    response_headers: undefined, source_ip: undefined, ip_source: undefined, debug_availability: "expired" };
}

function auditPivot(event: ActivityEvent): string | undefined {
  const target = event.activation_id ? ["activation", event.activation_id] : event.license_id ? ["license", event.license_id] : event.product_id ? ["product", event.product_id] : undefined;
  if (!target) return undefined;
  const time = Date.parse(event.captured_at);
  return `/admin/audit?${new URLSearchParams({ source: "api_activity", target_type: target[0], target_id: target[1], from: new Date(time - 300000).toISOString(), to: new Date(time + 300000).toISOString() })}`;
}
